/**
 * ─────────────────────────────────────────────────────────────────────
 * FILE: src/contexts/AdminAuthContext.tsx
 * DESCRIPTION: Context for managing admin authentication state across
 *              admin pages. Provides persistent admin login state.
 *
 * @copyright 2025-2026 Anou. All rights reserved.
 * @author Anou
 * @version 1.0.0
 * @created 2026-03-27
 * ─────────────────────────────────────────────────────────────────────
 */

import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface AdminAuthContextType {
  isAdminAuthenticated: boolean;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined);

const ADMIN_PASSWORD = 'admin2026'; // In production, use proper auth
const STORAGE_KEY = 'verimark_admin_auth';

export const AdminAuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  useEffect(() => {
    // Check for existing admin session on mount
    const storedAuth = localStorage.getItem(STORAGE_KEY);
    if (storedAuth) {
      const { timestamp, authenticated } = JSON.parse(storedAuth);
      // Session expires after 24 hours
      const isExpired = Date.now() - timestamp > 24 * 60 * 60 * 1000;
      if (authenticated && !isExpired) {
        setIsAdminAuthenticated(true);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    }
  }, []);

  const login = async (password: string): Promise<boolean> => {
    if (password === ADMIN_PASSWORD) {
      setIsAdminAuthenticated(true);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        authenticated: true,
        timestamp: Date.now(),
      }));
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAdminAuthenticated(false);
    localStorage.removeItem(STORAGE_KEY);
  };

  return (
    <AdminAuthContext.Provider value={{ isAdminAuthenticated, login, logout }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};
